import { AnalysisGateDispatch, BuildStatus, RestartProcessingOutcome } from '@cadstrom/cadstrom-shared';
import AddIcon from '@mui/icons-material/Add';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Tooltip from '@mui/material/Tooltip';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { useCallback, useMemo, useState } from 'react';

import { ProjectHeaderBillingStatus } from '#components/billing-usage/project-header-billing-status.js';
import { ProjectName } from '#components/project-name/project-name.js';
import { RelativeDate } from '#components/relative-timestamp/relative-date.js';
import { Toast } from '#components/toast/toast.js';
import { abilityOverrideAtom } from '#src/state/atoms/ability-override-atom.js';
import { useAuth0Context } from '#src/state/contexts/auth0-context.js';
import { useTRPC, useTRPCClient } from '#src/trpc.js';
import { Can } from '#state/contexts/ability-context.js';
import { useAddToast } from '#state/stores/toast-store.js';
import { CreateRevisionDialog } from './create-revision-dialog.js';
import { CurrentRevisionCard } from './current-revision-card.js';
import { downloadBuildSourceArchive } from './download-source-archive.js';
import { RevisionActionsMenu } from './revision-actions-menu.js';
import { RevisionEmptyState } from './revision-empty-state.js';
import { RevisionHistoryTimeline } from './revision-history-timeline.js';
import { RevisionLoadError } from './revision-load-error.js';
import { toRevisionTimeline } from './revision-timeline-utils.js';
import { StatusDialogVariant, StatusMessageDialog } from './status-message-dialog.js';
import { UnfinishedRevisionsDialog } from './unfinished-revisions-dialog.js';
import { getUnfinishedRevisions } from './unfinished-revisions.js';

import type { RevisionStatus, RevisionWithFiles } from '@cadstrom/cadstrom-shared';
import type { ReactNode } from 'react';

export const Route = createFileRoute('/_authorization-gate/orgs/$orgName/projects/$projectId/overview/')({
	staticData: {
		authorize: (ability) => ability.can('view', 'project'),
		navigation: {
			label: 'Project Overview',
			keywords: ['revisions', 'dashboard'],
		},
	},
	loader: async ({ context: { queryClient, getProjectPreviewOptions } }) => {
		const { projectPreview } = await queryClient.ensureQueryData(getProjectPreviewOptions);
		return { projectPreview };
	},
	head: ({ loaderData }) => ({
		meta: [{ title: (loaderData ? loaderData.projectPreview.name : 'Project') + ' • Cadstrom' }],
	}),
	component: ProjectOverviewPage,
});

function ProjectOverviewPage() {
	const { projectId, orgName } = Route.useParams();
	const { projectPreview } = Route.useLoaderData();
	const trpc = useTRPC();
	const trpcClient = useTRPCClient();
	const queryClient = useQueryClient();
	const auth0 = useAuth0Context();
	const addToast = useAddToast();

	const [abandonRevisionDialog, setAbandonRevisionDialog] = useState<{
		revisionId: string;
		displayName?: string;
	} | null>(null);
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [isUnfinishedDialogOpen, setIsUnfinishedDialogOpen] = useState(false);
	const [statusDialog, setStatusDialog] = useState<{
		revisionId: string;
		displayName?: string;
		variant: StatusDialogVariant;
		/** The status a staff force write will set; only for the ForceStatus variant. */
		targetStatus?: RevisionStatus;
	} | null>(null);
	const [updatingRevisionId, setUpdatingRevisionId] = useState<string | null>(null);

	const {
		data: revisionsData,
		isPending: revisionsPending,
		isError: revisionsError,
		refetch: refetchRevisions,
	} = useQuery(trpc.revision.list.queryOptions({ projectId }));
	const revisions = useMemo(() => revisionsData?.revisions ?? [], [revisionsData]);
	const unfinishedRevisions = useMemo(() => getUnfinishedRevisions(revisions), [revisions]);

	const { data: currentUser } = useQuery(trpc.user.getCurrent.queryOptions());
	const isStaff = currentUser?.auth.isStaff ?? false;

	// "View as user" leaves the session untouched, so the flag alone would keep
	// operator detail on screen while previewing the customer view.
	const abilityOverride = useAtomValue(abilityOverrideAtom);
	const showOperatorDetail = isStaff && abilityOverride.rules === null;

	const refreshRevision = async (revisionId: string) => {
		await Promise.all([
			queryClient.invalidateQueries({ queryKey: trpc.revision.list.queryKey() }),
			queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() }),
			queryClient.invalidateQueries({ queryKey: trpc.revision.get.queryKey({ id: revisionId }) }),
		]);
		setUpdatingRevisionId(null);
	};
	const reportRevisionMutationError = (error: { message: string }) => {
		setUpdatingRevisionId(null);
		addToast(
			<Toast severity='error' title='Revision update failed'>
				{error.message}
			</Toast>,
		);
	};

	const { mutate: forceRevisionStatus } = useMutation(
		trpc.revision.forceStatus.mutationOptions({
			onSuccess: (_data, variables) => refreshRevision(variables.id),
			onError: reportRevisionMutationError,
		}),
	);
	const { mutate: setRevisionStatusMessage } = useMutation(
		trpc.revision.setStatusMessage.mutationOptions({
			onSuccess: (_data, variables) => refreshRevision(variables.id),
			onError: reportRevisionMutationError,
		}),
	);
	const { mutate: setRevisionAutomationMode } = useMutation(
		trpc.revision.setAutomationMode.mutationOptions({
			onSuccess: (data, variables) => {
				// Enrolment and dispatch succeed independently, so a silent success here would
				// leave the switch on with nothing driving the revision.
				if (data.gateDispatch === AnalysisGateDispatch.NotStarted) {
					addToast(
						<Toast severity='warning' title='Fast mode is on, but analysis did not start'>
							Turn fast mode off and on again to retry.
						</Toast>,
					);
				}
				return refreshRevision(variables.id);
			},
			onError: reportRevisionMutationError,
		}),
	);
	const { mutate: restartRevisionProcessing } = useMutation(
		trpc.revision.restartProcessing.mutationOptions({
			onSuccess: (data, variables) => {
				addToast(
					<Toast
						severity='success'
						title={
							data.outcome === RestartProcessingOutcome.Started ?
								'Processing restarted'
							:	'Processing is already running'
						}
					/>,
				);
				return refreshRevision(variables.id);
			},
			onError: (error: { message: string }) => {
				setUpdatingRevisionId(null);
				addToast(
					<Toast severity='error' title='Restart failed'>
						{error.message}
					</Toast>,
				);
			},
		}),
	);

	const { mutate: abandonRevision, isPending: isAbandonPending } = useMutation(
		trpc.revision.abandonBuild.mutationOptions({
			onSuccess: async (data, variables) => {
				await Promise.all([
					queryClient.invalidateQueries({ queryKey: trpc.revision.list.queryKey() }),
					queryClient.invalidateQueries({ queryKey: trpc.revision.get.queryKey({ id: variables.id }) }),
				]);
				setAbandonRevisionDialog(null);
				if (!data.success) {
					addToast(
						<Toast severity='error' title='Revision not abandoned'>
							This revision can no longer be abandoned from its current status.
						</Toast>,
					);
				}
			},
		}),
	);

	const { mutate: abandonUnfinishedRevisions, isPending: isAbandonUnfinishedPending } = useMutation({
		mutationFn: (revisionIds: string[]) =>
			Promise.all(revisionIds.map((id) => trpcClient.revision.abandonBuild.mutate({ id }))),
		onSettled: (_data, _error, revisionIds) =>
			Promise.all([
				queryClient.invalidateQueries({ queryKey: trpc.revision.list.queryKey() }),
				queryClient.invalidateQueries({ queryKey: trpc.project.list.queryKey() }),
				...revisionIds.map((id) => queryClient.invalidateQueries({ queryKey: trpc.revision.get.queryKey({ id }) })),
			]),
		onSuccess: (results) => {
			setIsUnfinishedDialogOpen(false);
			if (results.every((result) => result.success)) {
				setIsCreateDialogOpen(true);
				return;
			}
			addToast(
				<Toast severity='error' title='Not every revision was abandoned'>
					At least one revision can no longer be abandoned from its current status.
				</Toast>,
			);
		},
		onError: (error) => {
			addToast(
				<Toast severity='error' title='Revisions not abandoned'>
					{error.message}
				</Toast>,
			);
		},
	});

	const handleNewRevision = () => {
		if (unfinishedRevisions.length > 0) {
			setIsUnfinishedDialogOpen(true);
		} else {
			setIsCreateDialogOpen(true);
		}
	};

	const {
		mutate: downloadSourceFiles,
		isPending: isDownloadPending,
		variables: downloadingRevisionId,
	} = useMutation({
		mutationFn: (revisionId: string) => downloadBuildSourceArchive(auth0.client, projectId, revisionId),
		onMutate: () => {
			addToast(
				<Toast severity='info' title='Preparing download'>
					Your source files are being packaged and will download automatically as soon as they are ready.
				</Toast>,
			);
		},
		onError: (error) => {
			addToast(
				<Toast severity='error' title='Failed to download source files'>
					{error instanceof Error ? error.message : 'An unexpected error occurred.'}
				</Toast>,
			);
		},
	});

	// Every staff status change goes through the reason dialog: it is a write outside
	// the revision lifecycle and is recorded with the reason given.
	const handleStatusChange = useCallback(
		(revisionId: string, displayName: string | undefined, newStatus: RevisionStatus) => {
			setStatusDialog({ revisionId, displayName, variant: StatusDialogVariant.ForceStatus, targetStatus: newStatus });
		},
		[],
	);

	const handleClearStatusMessage = useCallback(
		(revisionId: string) => {
			setUpdatingRevisionId(revisionId);
			setRevisionStatusMessage({ id: revisionId, statusMessage: '' });
		},
		[setRevisionStatusMessage],
	);

	const handleToggleAutomation = useCallback(
		(revisionId: string, enabled: boolean) => {
			setUpdatingRevisionId(revisionId);
			setRevisionAutomationMode({ id: revisionId, enabled });
		},
		[setRevisionAutomationMode],
	);

	const handleRestartProcessing = useCallback(
		(revisionId: string) => {
			setUpdatingRevisionId(revisionId);
			restartRevisionProcessing({ id: revisionId });
		},
		[restartRevisionProcessing],
	);

	const handleSaveStatusDialog = useCallback(
		(text: string) => {
			if (!statusDialog) {
				return;
			}
			setUpdatingRevisionId(statusDialog.revisionId);
			if (statusDialog.variant === StatusDialogVariant.ForceStatus && statusDialog.targetStatus !== undefined) {
				forceRevisionStatus({
					id: statusDialog.revisionId,
					status: statusDialog.targetStatus,
					reason: text,
					// An error reason is also what users see against the status.
					statusMessage: statusDialog.targetStatus === BuildStatus.Error ? text : undefined,
				});
			} else {
				setRevisionStatusMessage({ id: statusDialog.revisionId, statusMessage: text });
			}
			setStatusDialog(null);
		},
		[statusDialog, forceRevisionStatus, setRevisionStatusMessage],
	);

	const { current, history } = useMemo(() => toRevisionTimeline(revisions), [revisions]);

	const renderMenu = useCallback(
		(revision: RevisionWithFiles): ReactNode => (
			<RevisionActionsMenu
				revision={revision}
				isStaff={isStaff}
				isUpdating={updatingRevisionId === revision.id}
				isDownloading={isDownloadPending && downloadingRevisionId === revision.id}
				onSetStatus={(status) => handleStatusChange(revision.id, revision.displayName, status)}
				onEditStatusMessage={() =>
					setStatusDialog({
						revisionId: revision.id,
						displayName: revision.displayName,
						variant: StatusDialogVariant.Message,
					})
				}
				onClearStatusMessage={() => handleClearStatusMessage(revision.id)}
				onToggleAutomation={(enabled) => handleToggleAutomation(revision.id, enabled)}
				onRestartProcessing={() => handleRestartProcessing(revision.id)}
				onAbandon={() =>
					setAbandonRevisionDialog({
						revisionId: revision.id,
						displayName: revision.displayName,
					})
				}
				onDownloadSourceFiles={() => downloadSourceFiles(revision.id)}
			/>
		),
		[
			isStaff,
			updatingRevisionId,
			handleStatusChange,
			handleClearStatusMessage,
			handleToggleAutomation,
			handleRestartProcessing,
			downloadSourceFiles,
			isDownloadPending,
			downloadingRevisionId,
		],
	);

	let body: ReactNode;
	if (revisionsPending) {
		body = (
			<div className='flex flex-col gap-6'>
				<Skeleton variant='rounded' height={64} />
				<div className='flex flex-col gap-4'>
					<Skeleton variant='text' width={140} height={16} />
					<Skeleton variant='rounded' height={56} />
					<Skeleton variant='rounded' height={56} />
				</div>
			</div>
		);
	} else if (revisionsError && revisionsData === undefined) {
		body = <RevisionLoadError onRetry={() => void refetchRevisions()} />;
	} else if (current === undefined) {
		body = <RevisionEmptyState onCreateRevision={() => setIsCreateDialogOpen(true)} />;
	} else {
		body = (
			<div className='flex flex-col gap-8'>
				<CurrentRevisionCard
					revision={current}
					orgName={orgName}
					projectId={projectId}
					menu={renderMenu(current)}
					showOperatorDetail={showOperatorDetail}
					isStaff={showOperatorDetail}
				/>
				{history.length > 0 && (
					<RevisionHistoryTimeline
						revisions={history}
						orgName={orgName}
						projectId={projectId}
						renderMenu={renderMenu}
						isStaff={showOperatorDetail}
					/>
				)}
			</div>
		);
	}

	return (
		<>
			<div className='flex grow basis-0 flex-col overflow-y-auto'>
				<div className='mx-auto flex w-full max-w-[1120px] flex-col gap-6 px-4 py-6'>
					<header className='flex flex-col gap-2'>
						<div className='flex flex-wrap items-start justify-between gap-4'>
							<ProjectName isDemo={projectPreview.isDemo} gapClassName='gap-3'>
								<h1 className='text-5xl leading-[1.15] font-semibold tracking-[-0.01em] text-gray-a12'>
									{projectPreview.name}
								</h1>
							</ProjectName>
							<Can I='manage' a='project'>
								<Button
									variant='outlined'
									startIcon={<AddIcon />}
									onClick={handleNewRevision}
									disabled={revisionsPending}
								>
									<span>New revision</span>
								</Button>
							</Can>
						</div>
						<p className='flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm text-gray-a10'>
							<span>
								{revisions.length} revision{revisions.length === 1 ? '' : 's'}
							</span>
							<Tooltip title='A revision is one version of this design over time.'>
								<InfoOutlinedIcon
									tabIndex={0}
									aria-label='What is a revision?'
									className='text-[14px] text-gray-a8 transition-colors outline-none hover:text-gray-a10 focus-visible:text-gray-a10'
								/>
							</Tooltip>
							<span aria-hidden>·</span>
							<span>
								created{' '}
								<RelativeDate
									date={projectPreview.createdAt === null ? undefined : new Date(projectPreview.createdAt)}
								/>
							</span>
							<ProjectHeaderBillingStatus projectId={projectId} orgName={orgName} />
						</p>
					</header>

					{body}
				</div>
			</div>

			<CreateRevisionDialog
				open={isCreateDialogOpen}
				onClose={() => setIsCreateDialogOpen(false)}
				projectId={projectId}
			/>

			<UnfinishedRevisionsDialog
				open={isUnfinishedDialogOpen}
				revisions={unfinishedRevisions}
				isAbandoning={isAbandonUnfinishedPending}
				onClose={() => setIsUnfinishedDialogOpen(false)}
				onAbandon={() => abandonUnfinishedRevisions(unfinishedRevisions.map((revision) => revision.id))}
			/>

			<Dialog open={abandonRevisionDialog !== null} onClose={() => setAbandonRevisionDialog(null)}>
				<DialogTitle>Abandon revision?</DialogTitle>
				<DialogContent>
					<DialogContentText>
						Abandoning a revision will stop any in-progress analysis. This cannot be undone.
					</DialogContentText>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAbandonRevisionDialog(null)} autoFocus>
						<span>Cancel</span>
					</Button>
					<Button
						color='error'
						onClick={() => {
							if (abandonRevisionDialog) {
								abandonRevision({ id: abandonRevisionDialog.revisionId });
							}
						}}
						variant='contained'
						disabled={isAbandonPending}
						startIcon={isAbandonPending ? <CircularProgress size={16} color='inherit' /> : undefined}
					>
						<span>Abandon revision</span>
					</Button>
				</DialogActions>
			</Dialog>

			<StatusMessageDialog
				key={`${statusDialog?.revisionId}-${statusDialog?.variant}-${statusDialog?.targetStatus}`}
				open={statusDialog !== null}
				variant={statusDialog?.variant ?? StatusDialogVariant.Message}
				targetStatus={statusDialog?.targetStatus}
				displayName={statusDialog?.displayName}
				onClose={() => setStatusDialog(null)}
				onSave={handleSaveStatusDialog}
				initialMessage={
					statusDialog?.variant === StatusDialogVariant.Message ?
						(revisions.find((rev) => rev.id === statusDialog.revisionId)?.statusMessage ?? '')
					:	''
				}
			/>
		</>
	);
}
